import { mkdir, readFile, writeFile } from 'node:fs/promises';
import path, { posix as posixPath } from 'node:path';
import type { SandboxSession } from '../sandbox/session';
import type { SessionMemorySnapshot, SessionMemoryStore } from './types';

export class InMemorySessionMemoryStore implements SessionMemoryStore {
  readonly #snapshots = new Map<string, SessionMemorySnapshot>();

  async read(sessionId: string): Promise<SessionMemorySnapshot | null> {
    const snapshot = this.#snapshots.get(sessionId);
    return snapshot ? structuredClone(snapshot) : null;
  }

  async write(
    sessionId: string,
    snapshot: SessionMemorySnapshot,
  ): Promise<void> {
    this.#snapshots.set(sessionId, structuredClone(snapshot));
  }
}

export class FileSessionMemoryStore implements SessionMemoryStore {
  constructor(
    private readonly rootDir = path.join(
      process.cwd(),
      '.agents',
      'session-memory',
    ),
  ) {}

  async read(sessionId: string): Promise<SessionMemorySnapshot | null> {
    const dir = this.#sessionDir(sessionId);
    let content: string;
    try {
      content = await readFile(path.join(dir, 'memory.md'), 'utf8');
    } catch (error) {
      if (
        error &&
        typeof error === 'object' &&
        (error as { code?: unknown }).code === 'ENOENT'
      ) {
        return null;
      }
      throw error;
    }

    let meta: Partial<SessionMemorySnapshot> = {};
    try {
      meta = JSON.parse(
        await readFile(path.join(dir, 'memory.meta.json'), 'utf8'),
      );
    } catch (error) {
      if (
        !error ||
        typeof error !== 'object' ||
        (error as { code?: unknown }).code !== 'ENOENT'
      ) {
        throw error;
      }
    }

    return {
      content,
      lastSummarizedItemId:
        typeof meta.lastSummarizedItemId === 'string'
          ? meta.lastSummarizedItemId
          : undefined,
      updatedAt:
        typeof meta.updatedAt === 'string' ? meta.updatedAt : undefined,
      tokenCountAtUpdate:
        typeof meta.tokenCountAtUpdate === 'number'
          ? meta.tokenCountAtUpdate
          : undefined,
    };
  }

  async write(
    sessionId: string,
    snapshot: SessionMemorySnapshot,
  ): Promise<void> {
    const dir = this.#sessionDir(sessionId);
    await mkdir(dir, { recursive: true });
    await writeFile(path.join(dir, 'memory.md'), snapshot.content, 'utf8');
    await writeFile(
      path.join(dir, 'memory.meta.json'),
      `${JSON.stringify(
        {
          lastSummarizedItemId: snapshot.lastSummarizedItemId,
          updatedAt: snapshot.updatedAt,
          tokenCountAtUpdate: snapshot.tokenCountAtUpdate,
        },
        null,
        2,
      )}\n`,
      'utf8',
    );
  }

  #sessionDir(sessionId: string): string {
    return path.join(this.rootDir, encodeURIComponent(sessionId));
  }
}

export class SandboxSessionMemoryStore implements SessionMemoryStore {
  constructor(
    private readonly session: SandboxSession,
    private readonly options: {
      rootDir?: string;
      runAs?: string;
    } = {},
  ) {}

  async read(sessionId: string): Promise<SessionMemorySnapshot | null> {
    this.#assertReadSupport();
    const dir = this.#sessionDir(sessionId);
    let content: string;
    try {
      content = (
        await this.session.readTextFile!({
          path: posixPath.join(dir, 'memory.md'),
          runAs: this.options.runAs,
        })
      ).content;
    } catch (error) {
      if (isMissingFileError(error)) {
        return null;
      }
      throw error;
    }

    let meta: Partial<SessionMemorySnapshot> = {};
    try {
      meta = JSON.parse(
        (
          await this.session.readTextFile!({
            path: posixPath.join(dir, 'memory.meta.json'),
            runAs: this.options.runAs,
          })
        ).content,
      );
    } catch (error) {
      if (!isMissingFileError(error)) {
        throw error;
      }
    }

    return {
      content,
      lastSummarizedItemId:
        typeof meta.lastSummarizedItemId === 'string'
          ? meta.lastSummarizedItemId
          : undefined,
      updatedAt:
        typeof meta.updatedAt === 'string' ? meta.updatedAt : undefined,
      tokenCountAtUpdate:
        typeof meta.tokenCountAtUpdate === 'number'
          ? meta.tokenCountAtUpdate
          : undefined,
    };
  }

  async write(
    sessionId: string,
    snapshot: SessionMemorySnapshot,
  ): Promise<void> {
    if (!this.session.writeFile) {
      throw new Error(
        'SandboxSessionMemoryStore requires a sandbox session with writeFile().',
      );
    }
    const dir = this.#sessionDir(sessionId);
    await this.session.writeFile({
      path: posixPath.join(dir, 'memory.md'),
      content: snapshot.content,
      runAs: this.options.runAs,
    });
    await this.session.writeFile({
      path: posixPath.join(dir, 'memory.meta.json'),
      content: `${JSON.stringify(
        {
          lastSummarizedItemId: snapshot.lastSummarizedItemId,
          updatedAt: snapshot.updatedAt,
          tokenCountAtUpdate: snapshot.tokenCountAtUpdate,
        },
        null,
        2,
      )}\n`,
      runAs: this.options.runAs,
    });
  }

  #sessionDir(sessionId: string): string {
    return posixPath.join(
      this.options.rootDir ?? '.agents/session-memory',
      encodeURIComponent(sessionId),
    );
  }

  #assertReadSupport(): void {
    if (!this.session.readTextFile) {
      throw new Error(
        'SandboxSessionMemoryStore requires a sandbox session with readTextFile().',
      );
    }
  }
}

function isMissingFileError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const maybe = error as { code?: unknown; message?: unknown };
  return (
    maybe.code === 'ENOENT' ||
    (typeof maybe.message === 'string' &&
      /not found|does not exist|不存在|找不到|文件不存在/iu.test(maybe.message))
  );
}
